import React, { useEffect, useState } from 'react';
import { useFormContext, Controller, ControllerRenderProps, FieldValues } from 'react-hook-form';
import { withTranslation, WithTranslation } from 'react-i18next';
import Select, { MultiValue, SingleValue } from 'react-select';

interface SelectOption {
  label: string;
  value: string | number;
}

export interface InputProps extends Omit<WithTranslation, 't' | 'tReady'> {
  name: string;
  options?: SelectOption[];
  label?: string;
  classList?: string;
  labelwithin?: boolean;
  onblur?: () => void;
  required?: boolean;
  id?: string;
  type?: string;
  change?: (value: any) => void;
  isMulti?: boolean;
  placeholder?: string;
  value?: string | number;
  defaultValue?: any;
  disabled?: boolean;
  error?: string;
  t: (key: string) => string;
  tReady?: boolean;
  [key: string]: any;
}

const InputComponent: React.FC<InputProps> = (props) => {
  const {
    name,
    options,
    label,
    classList,
    labelwithin,
    t,
    tReady,
    onblur,
    required,
    id,
    type = 'text',
    change,
    isMulti = false,
    ...newProps
  } = props;

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { control, watch, trigger, register } = useFormContext();
  const value = watch(name);

  useEffect(() => {
    if (value) {
      trigger(name);
    }
  }, [value, trigger, name]);

  const handleSelectChange = (
    option: SingleValue<SelectOption> | MultiValue<SelectOption>,
    field: ControllerRenderProps<FieldValues, string>
  ) => {
    if (isMulti && Array.isArray(option)) {
      field.onChange(option.map(x => x.value));
    } else if (!isMulti && !Array.isArray(option) && option) {
      field.onChange((option as any)?.value);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={{ required }}
      render={({ field, fieldState: { error } }) => (
        <div className=''>
          {
            !labelwithin && <label htmlFor={name} className="text-secondary text-sm inline-block mb-2">{label}</label>
          }
          {
            !options?.length ? (
              <div className="input-wrapper relative">
                {type === 'textarea' ? (
                  <textarea
                    {...newProps}
                    className={`w-full border p-3 text-black bg-input rounded-md ${classList ?? ''} ${labelwithin ? 'labelwithin' : ''} ${error ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={labelwithin ? label : newProps.placeholder}
                    onChange={field.onChange}
                    onBlur={onblur}
                    id={id}
                    value={field.value ?? newProps.value ?? ''}
                    disabled={newProps.disabled}
                    required={required}
                    rows={newProps.rows || 4}
                  />
                ) : (
                  <input
                    {...newProps}
                    className={`w-full border text-black p-3 bg-input rounded-md ${classList ?? ''} ${labelwithin ? 'labelwithin' : ''} ${error ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={labelwithin ? label : newProps.placeholder}
                    {...register(name, { required })}
                    onChange={field.onChange}
                    onBlur={onblur}
                    id={id}
                    value={field.value ?? newProps.value ?? ''}
                    type={type !== 'password' ? type : (showPassword ? 'text' : 'password')}
                    disabled={newProps.disabled}
                    required={required}
                  />
                )}
                {type === 'password' && (
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                )}
                {error && (
                  <p className="text-xs text-red-500 mt-1">
                    {error.type ? t(`misc.validations.${error.type}`) : newProps?.error}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-white">
                <Select
                  options={options}
                  name={name}
                  onChange={(option) => handleSelectChange(option, field)}
                  onBlur={field.onBlur}

                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      width: '100%',
                      // display: 'flex',
                      padding: '5px 10px',
                      color: '#000',
                      backgroundColor: '#f5f5f5',
                      borderColor: error ? '#ef4444' : baseStyles.borderColor,
                      '&:hover': {
                        borderColor: error ? '#ef4444' : baseStyles.borderColor
                      }
                    }),
                    container: (baseStyles) => ({
                      ...baseStyles,
                      //   position: 'relative',
                      //   borderRadius: '5px',
                      border: 'none',
                      //   color: '#000',
                      backgroundColor: '#5d4',
                      //   // height: isMulti ? 'auto' : '55px',
                    }),
                    input: (baseStyles) => ({
                      ...baseStyles,
                      //   position: 'absolute',
                      //   height: '45px !important',
                      //   width: '100% !important',
                      // backgroundColor: '#e35',
                      //   color: '#000',
                      //   margin: '0',
                      //   top: '50%',
                      //   transform: 'translateY(-50%)',
                      //   // backgroundColor: 'inherit',
                      //   border: 'none',
                    })
                  }}
                  components={{
                    IndicatorSeparator: () => null,

                  }}
                  defaultValue={newProps?.defaultValue}
                  placeholder={newProps?.placeholder}
                  className={`w-full bg-input rounded-md border ${error ? 'border-red-500' : 'border-gray-300'}`}
                  isMulti={isMulti}
                  isDisabled={newProps.disabled}
                />
                {error && (
                  <p className="text-xs text-red-500 mt-1">
                    {error.type ? t(`misc.validations.${error.type}`) : newProps?.error}
                  </p>
                )}
              </div>
            )
          }
        </div>
      )}
    />
  );
};

// 2️⃣ Wrap with translation HOC
const TranslatedInput = withTranslation()(InputComponent);

// 3️⃣ Remove translation props from required props
type InputPropsWithoutTranslation = Omit<InputProps, keyof WithTranslation | 't' | 'tReady'>;

// 4️⃣ Keep type clean without memo
const Input = TranslatedInput as unknown as React.FC<InputPropsWithoutTranslation>;

// 5️⃣ Export
export default Input;
